import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { posts as postsApi, comments as commentsApi } from "../../services/api";
import { seedPosts, getUserById as getSeedUser } from "../../data/seedData";
import { optimizedUrl } from "../../utils/cloudinary";
import { ArrowLeft, MapPin, Info, Send, Loader, RotateCcw, ThumbsUp, ThumbsDown, Calendar, Eye, Flag, Pencil, Check, X, Trash2, UtensilsCrossed, Share2 } from "lucide-react";
import SimilarityBadge from "../SimilarityBadge";
import ReportDialog from "../ReportDialog";
import { sharePost } from "../../utils/share";
import { getPostSchema } from "../../utils/seo";
import { getCredibilityLabel, getCredibilityStyle } from "../../utils/cuisine";
import CuisinePicker from "../Upload/CuisinePicker";
import { useTranslation } from "react-i18next";

function PostDetailView() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, token, isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [commentError, setCommentError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editCaption, setEditCaption] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editWorthIt, setEditWorthIt] = useState(null);
  const [editWouldReturn, setEditWouldReturn] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editCuisine, setEditCuisine] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCuisineInfo, setShowCuisineInfo] = useState(false);
  const [shareStatus, setShareStatus] = useState(null);
  const [reporting, setReporting] = useState(null); // { contentType, contentId }
  const cuisineInfoRef = useRef(null);

  useEffect(() => {
    if (!showCuisineInfo) return;
    const handleClick = (e) => {
      if (cuisineInfoRef.current && !cuisineInfoRef.current.contains(e.target)) {
        setShowCuisineInfo(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [showCuisineInfo]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const postData = await postsApi.getById(postId, token);
        setPost(postData);
        const commentsData = await commentsApi.getByPost(postId);
        setComments(commentsData);
      } catch {
        const seedPost = seedPosts.find((p) => p.id === postId);
        if (seedPost) {
          setPost({ ...seedPost, user: getSeedUser(seedPost.userId) });
        }
        setComments([]);
      }
      setLoading(false);
    }
    load();
  }, [postId, token]);

  useEffect(() => {
    if (post) {
      const loc = post.locationName || "somewhere";
      document.title = `${post.caption?.slice(0, 50) || "Post"} — ${loc} | Pinned`;
    }
    return () => { document.title = t("app.title"); };
  }, [post, t]);

  useEffect(() => {
    const schema = post ? getPostSchema(post) : null;
    if (!schema) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    script.id = "json-ld-post";
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, [post]);

  const handleSubmitComment = async () => {
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    setCommentError(null);
    try {
      const comment = await commentsApi.create(token, postId, newComment.trim());
      setComments((prev) => [...prev, comment]);
      setNewComment("");
    } catch (err) {
      setCommentError(err.message || t("post.failedComment"));
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-th-bg">
        <Loader size={20} className="animate-spin text-th-accent" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="h-full flex items-center justify-center bg-th-bg">
        <p className="text-th-muted">{t("post.notFound")}</p>
      </div>
    );
  }

  const isOwner = currentUser && post.user && (post.userId === currentUser.id || post.user.id === currentUser.id);

  const startEditing = () => {
    setEditCaption(post.caption || "");
    setEditCategory(post.category || "food");
    setEditCuisine(post.cuisine || null);
    setEditWorthIt(post.worthIt);
    setEditWouldReturn(post.wouldReturn);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const updated = await postsApi.update(token, post.id, {
        caption: editCaption.trim(),
        category: editCategory,
        cuisine: editCategory === "food" ? editCuisine : null,
        worthIt: editWorthIt,
        wouldReturn: editWouldReturn,
      });
      setPost({ ...post, ...updated });
      setEditing(false);
    } catch (err) {
      setCommentError(err.message || t("post.failedSave"));
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await postsApi.delete(token, post.id);
      navigate(-1);
    } catch (err) {
      setCommentError(err.message || t("post.failedDelete"));
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleShare = async () => {
    const result = await sharePost(post);
    if (result === "copied") {
      setShareStatus("copied");
      setTimeout(() => setShareStatus(null), 2000);
    }
  };

  const poster = post.user;

  return (
    <div className="h-full flex flex-col bg-th-bg">
      <div className="flex-1 overflow-y-auto">
      <div className="max-w-xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="fixed top-[max(0.75rem,env(safe-area-inset-top))] left-3 z-30 w-9 h-9 bg-black/60 backdrop-blur-sm text-white rounded-xl flex items-center justify-center border border-white/10"
        >
          <ArrowLeft size={16} />
        </button>
        {!editing && isOwner && (
          <div className="fixed top-[max(0.75rem,env(safe-area-inset-top))] right-3 z-30 flex gap-2">
            <button
              onClick={startEditing}
              className="w-9 h-9 bg-black/60 backdrop-blur-sm text-white rounded-xl flex items-center justify-center border border-white/10"
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-9 h-9 bg-black/60 backdrop-blur-sm text-white rounded-xl flex items-center justify-center border border-white/10"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}

        {showDeleteConfirm && (
          <div className="fixed inset-0 z-[2000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
            <div className="bg-th-card border border-th-border rounded-2xl p-5 max-w-sm w-full text-center">
              <p className="text-sm text-th-text font-medium">{t("post.deleteConfirm")}</p>
              <div className="flex gap-3 mt-4 justify-center">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex items-center gap-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded-xl disabled:opacity-50 transition-colors"
                >
                  {deleting ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  {t("post.delete")}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="text-sm font-medium text-th-muted bg-th-surface border border-th-border px-4 py-2 rounded-xl disabled:opacity-50 transition-colors"
                >
                  {t("post.cancel")}
                </button>
              </div>
            </div>
          </div>
        )}

        {shareStatus === "copied" && (
          <div className="fixed top-[max(3.5rem,calc(env(safe-area-inset-top)+2.75rem))] left-1/2 -translate-x-1/2 z-30 bg-th-card border border-th-border text-th-text text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg animate-fadeIn">
            {t("share.copied")}
          </div>
        )}

        <img
          src={optimizedUrl(post.imageUrl)}
          alt={post.caption}
          className="w-full max-h-[70vh] object-cover md:mt-12"
        />

        <div className="px-4 py-4">
          {poster && (
            <div className="flex items-center justify-between">
              <Link
                to={`/profile/${poster.username}`}
                className="flex items-center gap-2.5"
              >
                {poster.avatarUrl ? (
                  <img
                    src={poster.avatarUrl}
                    alt={poster.username}
                    className="w-10 h-10 rounded-xl object-cover border border-th-border"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-th-surface border border-th-border flex items-center justify-center text-sm text-th-muted font-mono font-bold">
                    {poster.username?.[0]?.toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-th-text">@{poster.username}</p>
                  {poster.travelStyles?.length > 0 && (
                    <div className="flex gap-1.5">
                      {poster.travelStyles.slice(0, 3).map((s) => (
                        <span key={s} className="text-[11px] text-th-muted font-mono">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
              <div className="flex items-center gap-2">
                {currentUser && (
                  <SimilarityBadge viewer={currentUser} target={poster} align="right" compact />
                )}
                <span className="text-[11px] font-mono uppercase tracking-wider bg-th-surface border border-th-border text-th-muted px-2.5 py-1 rounded-lg">
                  {t(`categories.${post.category}`, post.category)}
                </span>
              </div>
            </div>
          )}

          {editing ? (
            <>
              <textarea
                value={editCaption}
                onChange={(e) => setEditCaption(e.target.value)}
                maxLength={280}
                rows={2}
                className="w-full mt-2 text-sm text-th-text bg-th-surface border border-th-border rounded-xl px-3 py-2 focus:outline-none focus:border-th-accent-border resize-none"
              />
              <div className="flex gap-2 mt-2">
                {["food", "stay", "adventure"].map((c) => (
                  <button key={c} onClick={() => { setEditCategory(c); if (c !== "food") setEditCuisine(null); }} className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-lg border transition-colors ${editCategory === c ? "bg-th-accent text-th-bg border-th-accent" : "bg-th-surface text-th-muted border-th-border"}`}>
                    {t(`categories.${c}`, c)}
                  </button>
                ))}
              </div>
              {editCategory === "food" && (
                <div className="mt-2">
                  <p className="text-[11px] font-mono uppercase tracking-wider text-th-muted mb-1">{t("upload.cuisine")}</p>
                  <CuisinePicker value={editCuisine} onChange={setEditCuisine} />
                </div>
              )}
              <div className="flex gap-2 mt-2">
                <button onClick={() => setEditWorthIt(!editWorthIt)} className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase px-2 py-1 rounded-md border ${editWorthIt ? "text-emerald-500 border-emerald-500/30 bg-emerald-500/10" : "text-red-400 border-red-400/30 bg-red-400/10"}`}>
                  {editWorthIt ? <ThumbsUp size={9} /> : <ThumbsDown size={9} />}
                  {editWorthIt ? t("post.worthIt") : t("post.notWorthIt")}
                </button>
                <button onClick={() => setEditWouldReturn(!editWouldReturn)} className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase px-2 py-1 rounded-md border ${editWouldReturn ? "text-emerald-500 border-emerald-500/30 bg-emerald-500/10" : "text-amber-500 border-amber-500/30 bg-amber-500/10"}`}>
                  <RotateCcw size={9} />
                  {editWouldReturn ? t("post.wouldReturn") : t("post.onceEnough")}
                </button>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={saveEdit} disabled={saving || !editCaption.trim()} className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium text-th-bg bg-th-accent py-2.5 rounded-xl disabled:opacity-40">
                  {saving ? <Loader size={14} className="animate-spin" /> : <Check size={14} />} {t("post.save")}
                </button>
                <button onClick={cancelEditing} className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium text-th-muted bg-th-surface border border-th-border py-2.5 rounded-xl">
                  <X size={14} /> {t("post.cancel")}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-th-text mt-3">{post.caption}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                {post.locationName && (
                  <p className="text-xs text-th-muted flex items-center gap-1 font-mono">
                    <MapPin size={10} />
                    {post.locationName}
                  </p>
                )}
                {(post.takenAt || post.createdAt) && (
                  <p className="text-xs text-th-muted flex items-center gap-1 font-mono">
                    <Calendar size={10} />
                    {new Date(post.takenAt || post.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {post.worthIt !== null && post.worthIt !== undefined && (
                  <span className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md border ${
                    post.worthIt
                      ? "text-emerald-500 border-emerald-500/30 bg-emerald-500/10"
                      : "text-red-400 border-red-400/30 bg-red-400/10"
                  }`}>
                    {post.worthIt ? <ThumbsUp size={9} /> : <ThumbsDown size={9} />}
                    {post.worthIt ? t("post.worthIt") : t("post.notWorthIt")}
                  </span>
                )}
                {post.wouldReturn !== null && post.wouldReturn !== undefined && (
                  <span className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md border ${
                    post.wouldReturn
                      ? "text-emerald-500 border-emerald-500/30 bg-emerald-500/10"
                      : "text-amber-500 border-amber-500/30 bg-amber-500/10"
                  }`}>
                    {post.wouldReturn ? <RotateCcw size={9} /> : <RotateCcw size={9} />}
                    {post.wouldReturn ? t("post.wouldReturn") : t("post.onceEnough")}
                  </span>
                )}
                {post.viewCount != null && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-th-muted px-2 py-0.5 rounded-md border border-th-border bg-th-surface">
                    <Eye size={9} />
                    {t("post.view", { count: post.viewCount })}
                  </span>
                )}
                {post.firstToPin && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-amber-400 px-2 py-0.5 rounded-md border border-amber-400/30 bg-amber-400/10">
                    <Flag size={9} />
                    {t("post.firstToPin")}
                  </span>
                )}
                {post.cuisine && post.cuisineCount != null && (() => {
                  const cred = getCredibilityLabel(post.cuisineCount);
                  const style = getCredibilityStyle(cred.tier);
                  return (
                    <span className="relative inline-flex items-center gap-1" ref={cuisineInfoRef}>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md border ${style.color} ${style.bg} ${style.border}`}>
                        <UtensilsCrossed size={9} />
                        {post.cuisine} · {t(cred.labelKey)} ({post.cuisineCount})
                        <button
                          onClick={() => setShowCuisineInfo(!showCuisineInfo)}
                          className="ml-0.5 p-2 -m-1.5 hover:opacity-70 transition-opacity"
                        >
                          <Info size={9} />
                        </button>
                      </span>
                      {showCuisineInfo && (
                        <div className="absolute left-0 top-full mt-1.5 z-20 w-56 bg-th-surface border border-th-border rounded-xl p-3 shadow-lg shadow-black/20">
                          <p className="text-xs text-th-text font-medium mb-2">{t("credibility.title")}</p>
                          <div className="space-y-1.5 text-[11px] text-th-text-sec">
                            <p><span className="text-th-muted">{t("credibility.triedIt")}</span> (1–5) — {t("credibility.tier1")}</p>
                            <p><span className="text-blue-400">{t("credibility.regular")}</span> (6–15) — {t("credibility.tier2")}</p>
                            <p><span className="text-amber-400">{t("credibility.expert")}</span> (16+) — {t("credibility.tier3")}</p>
                          </div>
                        </div>
                      )}
                    </span>
                  );
                })()}
              </div>
            </>
          )}

          <div className="mt-3 flex items-center gap-4">
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 py-2.5 -my-2.5 px-1 -mx-1 text-xs font-medium text-th-accent hover:text-th-accent/80 transition-colors"
            >
              <Share2 size={14} />
              {t("share.share", "Share")}
            </button>
            {isAuthenticated && !isOwner && (
              <button
                onClick={() => setReporting({ contentType: "post", contentId: post.id })}
                className="flex items-center gap-1.5 py-2.5 -my-2.5 px-1 -mx-1 text-xs font-medium text-th-muted hover:text-th-text-sec transition-colors"
              >
                <Flag size={13} />
                {t("report.reportPost")}
              </button>
            )}
          </div>

          <div className="mt-4 border-t border-th-border pt-4">
            <h3 className="text-xs font-mono uppercase tracking-wider text-th-muted mb-3">
              {t("post.comments")} {comments.length > 0 && `(${comments.length})`}
            </h3>
            {comments.length === 0 ? (
              <p className="text-xs text-th-muted py-4 text-center">
                {t("post.noComments")}
              </p>
            ) : (
              <div className="space-y-3">
                {comments.map((c) => {
                  const commenter = c.user;
                  return (
                    <div key={c.id} className="flex gap-2.5">
                      <Link to={`/profile/${commenter?.username || c.userId}`}>
                        {commenter?.avatarUrl ? (
                          <img
                            src={commenter.avatarUrl}
                            alt=""
                            className="w-7 h-7 rounded-lg object-cover flex-shrink-0 border border-th-border"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-lg bg-th-surface border border-th-border flex-shrink-0 flex items-center justify-center text-[11px] text-th-muted font-mono">
                            {commenter?.username?.[0]?.toUpperCase() || "?"}
                          </div>
                        )}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-th-text-sec">
                          <Link to={`/profile/${commenter?.username || c.userId}`} className="font-semibold text-th-text hover:text-th-accent transition-colors">
                            @{commenter?.username || "unknown"}
                          </Link>{" "}
                          {c.body}
                        </p>
                        <p className="text-[11px] text-th-muted mt-0.5 font-mono">
                          {new Date(c.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      {isAuthenticated && currentUser?.id !== c.userId && (
                        <button
                          onClick={() => setReporting({ contentType: "comment", contentId: c.id })}
                          className="self-start p-2.5 -m-2 text-th-muted/60 hover:text-th-muted transition-colors"
                          title={t("report.reportComment")}
                        >
                          <Flag size={11} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {reporting && (
        <ReportDialog
          contentType={reporting.contentType}
          contentId={reporting.contentId}
          onClose={() => setReporting(null)}
        />
      )}

      {isAuthenticated ? (
        <div className="max-w-xl mx-auto w-full">
        <div className="border-t border-th-border bg-th-card px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {commentError && (
            <p className="text-xs text-th-danger mb-2">{commentError}</p>
          )}
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={t("post.askSomething")}
              maxLength={500}
              className="flex-1 text-sm px-4 py-2.5 bg-th-surface border border-th-border rounded-xl text-th-text placeholder-th-muted focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmitComment();
                }
              }}
            />
            <button
              onClick={handleSubmitComment}
              disabled={!newComment.trim() || submitting}
              className="w-10 h-10 flex items-center justify-center bg-th-accent text-th-bg rounded-xl disabled:opacity-30 transition-opacity"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
        </div>
      ) : (
        <div className="max-w-xl mx-auto w-full">
        <div className="border-t border-th-border px-4 py-3 text-center pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <Link to="/login" className="text-sm text-th-accent font-medium hover:underline transition-all">
            {t("post.loginToComment")}
          </Link>
        </div>
        </div>
      )}
    </div>
  );
}

export default PostDetailView;
